/**
 * A2UI Component Catalog
 *
 * Extends the Pentatonic component registry with:
 * - Layout components (Row, Column, Grid)
 * - Visualization wrappers (Chart, DataTable, Metric)
 * - Text component with variants
 */

import type { ComponentType, ReactNode } from 'react';
import { componentRegistry } from './component-registry';
import TimeSeriesChart from '../components/visualizations/TimeSeriesChart';
import DistributionChart from '../components/visualizations/DistributionChart';
import DataTable from '../components/visualizations/DataTable';
import MetricCard from '../components/visualizations/MetricCard';
import { useDrillDown } from '../hooks/useDrillDown';
import type { ChartProps, DataTableProps, MetricProps, RowProps, ColumnProps, GridProps, TextProps } from '../types/a2ui';

// ============================================================================
// Layout Components
// ============================================================================

const gapClasses: Record<string, string> = {
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-8',
};

const alignClasses: Record<string, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const justifyClasses: Record<string, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
};

/**
 * Row - Horizontal flex container
 */
function Row({ children, gap = 'md', align = 'start', justify = 'start', wrap = false }: RowProps) {
  const gapClass = typeof gap === 'string' ? gapClasses[gap] || 'gap-4' : `gap-${gap}`;
  const alignClass = alignClasses[align] || '';
  const justifyClass = justifyClasses[justify] || '';
  const wrapClass = wrap ? 'flex-wrap' : '';

  return (
    <div className={`flex ${gapClass} ${alignClass} ${justifyClass} ${wrapClass}`.trim()}>
      {children}
    </div>
  );
}

/**
 * Column - Vertical flex container
 */
function Column({ children, gap = 'md', align = 'stretch' }: ColumnProps) {
  const gapClass = typeof gap === 'string' ? gapClasses[gap] || 'gap-4' : `gap-${gap}`;
  const alignClass = alignClasses[align] || '';

  return (
    <div className={`flex flex-col ${gapClass} ${alignClass}`.trim()}>
      {children}
    </div>
  );
}

/**
 * Grid - CSS Grid container
 */
function Grid({ children, columns = 2, gap = 'md' }: GridProps) {
  const gapClass = typeof gap === 'string' ? gapClasses[gap] || 'gap-4' : `gap-${gap}`;
  const colsClass = `grid-cols-${columns}`;

  return (
    <div className={`grid ${colsClass} ${gapClass}`.trim()}>
      {children}
    </div>
  );
}

// ============================================================================
// Text Component
// ============================================================================

const textVariantClasses: Record<string, string> = {
  h1: 'text-4xl font-light text-black leading-tight',
  h2: 'text-2xl font-light text-black leading-tight',
  h3: 'text-xl font-light text-black leading-tight',
  body: 'text-base font-light text-black leading-relaxed',
  caption: 'font-mono text-xs text-grey-medium uppercase tracking-wider',
  code: 'font-mono text-sm bg-grey-light px-2 py-1',
};

/**
 * Text - Text display with variants
 */
function Text({ children, variant = 'body' }: TextProps) {
  const className = textVariantClasses[variant] || textVariantClasses.body;

  // Use appropriate HTML element based on variant
  switch (variant) {
    case 'h1':
      return <h1 className={className}>{children}</h1>;
    case 'h2':
      return <h2 className={className}>{children}</h2>;
    case 'h3':
      return <h3 className={className}>{children}</h3>;
    case 'code':
      return <code className={className}>{children}</code>;
    case 'caption':
      return <span className={className}>{children}</span>;
    default:
      return <p className={className}>{children}</p>;
  }
}

// ============================================================================
// Visualization Wrappers
// ============================================================================

/**
 * Aggregate raw data by a key field for bar/pie charts
 * E.g., items grouped by current_stage → [{current_stage: "returned", count: 50}, ...]
 */
function aggregateData(data: Record<string, unknown>[], groupKey: string, countKey: string): Record<string, unknown>[] {
  const counts: Record<string, number> = {};

  for (const item of data) {
    const key = String(item[groupKey] || 'unknown');
    counts[key] = (counts[key] || 0) + 1;
  }

  return Object.entries(counts).map(([key, count]) => ({
    [groupKey]: key,
    [countKey]: count,
  }));
}

/**
 * Check if data needs aggregation (has raw items vs pre-aggregated counts)
 */
function needsAggregation(data: Record<string, unknown>[], countKey: string): boolean {
  if (data.length === 0) return false;
  const firstItem = data[0];
  if (!firstItem) return false;
  // If first item doesn't have a count key, we need to aggregate
  return !(countKey in firstItem);
}

/**
 * Chart - Wrapper for TimeSeriesChart and DistributionChart
 * Auto-aggregates raw data for bar/pie charts if needed
 */
function Chart({ chartType, title, xAxis, yAxis, data, showGrid, showLegend }: ChartProps) {
  const { handleDrillDown } = useDrillDown();
  // Handle data that might be { items: [...] } or just an array
  let rawData: Record<string, unknown>[] = [];
  if (Array.isArray(data)) {
    rawData = data as Record<string, unknown>[];
  } else if (data && typeof data === 'object' && 'items' in data) {
    const items = (data as Record<string, unknown>).items;
    if (Array.isArray(items)) {
      rawData = items as Record<string, unknown>[];
    }
  }

  // Normalize axis config
  const xAxisConfig = {
    key: xAxis?.key || 'x',
    label: xAxis?.label || xAxis?.key || 'X',
    format: xAxis?.format,
  };
  const yAxisConfig = {
    key: yAxis?.key || 'count',
    label: yAxis?.label || yAxis?.key || 'Count',
    format: yAxis?.format,
  };

  // For bar/pie charts, auto-aggregate if data looks like raw items
  let chartData = rawData;
  if ((chartType === 'bar' || chartType === 'horizontalBar' || chartType === 'pie') && rawData.length > 0) {
    if (needsAggregation(rawData, yAxisConfig.key)) {
      chartData = aggregateData(rawData, xAxisConfig.key, yAxisConfig.key);
    }
  }

  if (chartType === 'line' || chartType === 'area') {
    return (
      <TimeSeriesChart
        title={title}
        data={chartData}
        xAxis={xAxisConfig}
        yAxis={yAxisConfig}
        chartType={chartType}
        showGrid={showGrid}
        showLegend={showLegend}
      />
    );
  }

  if (chartType === 'bar' || chartType === 'horizontalBar' || chartType === 'pie') {
    return (
      <DistributionChart
        title={title}
        data={chartData}
        xAxis={xAxisConfig}
        yAxis={yAxisConfig}
        chartType={chartType}
        showLegend={showLegend}
        onSegmentClick={handleDrillDown}
      />
    );
  }

  return (
    <div className="p-4 bg-grey-light text-grey-medium">
      Unknown chart type: {chartType}
    </div>
  );
}

/**
 * DataTableWrapper - Wrapper for DataTable component
 */
function DataTableWrapper({ columns, data, sortable, paginated, pageSize, searchable, title }: DataTableProps & { title?: string }) {
  return (
    <DataTable
      title={title}
      config={{
        columns: columns || [],
        sortable: sortable ?? true,
        paginated: paginated ?? true,
        pageSize: pageSize ?? 10,
        searchable: searchable ?? false,
      }}
      data={Array.isArray(data) ? data as Record<string, unknown>[] : []}
    />
  );
}

/**
 * MetricWrapper - Wrapper for MetricCard component
 */
function Metric({ value, label, format, icon, trend }: MetricProps) {
  return (
    <MetricCard
      config={{
        value: value ?? 0,
        label: label || 'Metric',
        format: format,
        icon: icon,
        trend: trend,
      }}
    />
  );
}

// ============================================================================
// Container Component (generic wrapper)
// ============================================================================

interface ContainerProps {
  children?: ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  background?: 'white' | 'grey' | 'black';
}

const paddingClasses: Record<string, string> = {
  none: '',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-8',
};

const bgClasses: Record<string, string> = {
  white: 'bg-white',
  grey: 'bg-grey-light',
  black: 'bg-black text-white',
};

function Container({ children, className = '', padding = 'md', background = 'white' }: ContainerProps) {
  const padClass = paddingClasses[padding] || '';
  const bgClass = bgClasses[background] || '';

  return (
    <div className={`${padClass} ${bgClass} ${className}`.trim()}>
      {children}
    </div>
  );
}

// ============================================================================
// Complete A2UI Catalog
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const a2uiCatalog: Record<string, ComponentType<any>> = {
  // All Pentatonic components
  ...componentRegistry,

  // Layout components
  Row,
  Column,
  Grid,
  Container,

  // Text component
  Text,

  // Visualization wrappers
  Chart,
  DataTable: DataTableWrapper,
  Metric,

  // Aliases for common patterns
  Flex: Row,
  Stack: Column,
};

/**
 * Check if component exists in catalog
 */
export function hasA2UIComponent(name: string): boolean {
  return name in a2uiCatalog;
}

/**
 * Get component by name
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getA2UIComponent(name: string): ComponentType<any> | undefined {
  return a2uiCatalog[name];
}

/**
 * List all available component names
 */
export function listA2UIComponents(): string[] {
  return Object.keys(a2uiCatalog).sort();
}

export default a2uiCatalog;
